// src/stations/scanner/misconceptionAnalyzer.ts — KS2 Misconception Diagnostician (T-6.1.2, FR-SCAN-001)
// Classifies pupil error patterns across the KS2 national curriculum and 11+ syllabus,
// mapping them to official KS2 objective codes with supportive Socratic guidance.

import type { UploadPayload } from './uploader';
import type { DiagnosedMisconception } from '@storage/scannerStorage';

export class MisconceptionAnalyzer {
  /**
   * Diagnoses learning gaps from worksheet text, image description, or filename.
   * Can be connected to the serverless Gemini endpoint or run client-side heuristically.
   */
  static analyze(payload: UploadPayload): DiagnosedMisconception[] {
    const text = `${payload.title} ${payload.fileName || ''} ${payload.rawTextContent || ''}`.toLowerCase();
    const results: DiagnosedMisconception[] = [];

    // 1. Fractions & Decimals Misconception (4F2)
    if (
      text.includes('fraction') ||
      text.includes('denominator') ||
      text.includes('numerator') ||
      text.includes('1/3') ||
      text.includes('1/2') ||
      text.includes('2/5') ||
      text.includes('3/8')
    ) {
      results.push({
        id: `misc_fraction_${Date.now()}`,
        ks2Code: '4F2',
        ks2Domain: 'Fractions & Decimals',
        title: 'Direct Denominator Addition Error',
        description:
          'Pupil adds both numerators and denominators across fractions without finding a common denominator.',
        errorPattern:
          'Added denominators directly (e.g. 1/3 + 1/2 = 2/5). Slices were combined without equalizing their size.',
        socraticInsight:
          'Remember Clawey’s reef pizza rule: the bottom number (denominator) tells you the size of each slice! You can only add the tops (numerators) once the denominators are identical (2/6 + 3/6 = 5/6).',
      });
    }

    // 2. Angles & Protractor Misconception (5G4)
    if (
      text.includes('angle') ||
      text.includes('protractor') ||
      text.includes('degree') ||
      text.includes('acute') ||
      text.includes('obtuse') ||
      text.includes('130°') ||
      text.includes('reflex')
    ) {
      results.push({
        id: `misc_angles_${Date.now()}`,
        ks2Code: '5G4',
        ks2Domain: 'Angles & Geometry',
        title: 'Protractor Scale Inversion Error',
        description:
          'Reading the outer obtuse scale instead of the inner acute scale on a 180° protractor.',
        errorPattern:
          'Recorded 130° for an acute angle that was actually 50°. Inverted inner vs outer circular scale readouts.',
        socraticInsight:
          'Check with your lobster pincers before measuring! If the jaws are narrower than an L-shape (90° square corner), the answer must be under 90°.',
      });
    }

    // 3. SPAG & Homophones / Fronted Adverbials (4E1)
    if (
      text.includes('they\'re') ||
      text.includes('their') ||
      text.includes('there') ||
      text.includes('spag') ||
      text.includes('homophone') ||
      text.includes('adverbial') ||
      text.includes('comma') ||
      text.includes('spelling')
    ) {
      results.push({
        id: `misc_spag_${Date.now()}`,
        ks2Code: '4E1',
        ks2Domain: 'SPAG & Punctuation',
        title: 'Homophone Confusion & Missing Adverbial Comma',
        description:
          'Confusing possessive "their", contraction "they\'re", and adverb "there", or omitting commas after fronted adverbials.',
        errorPattern:
          'Used "they\'re" instead of possessive "their", and missed comma pause after introductory adverbial phrase.',
        socraticInsight:
          'Use Clawey’s substitution trick: try saying "they are" in your sentence. If "they are" sounds clumsy, it’s possessive "their" with an "i" for Tudor’s treasure!',
      });
    }

    // 4. Perimeter vs Area Confusion (4M7)
    if (
      text.includes('perimeter') ||
      text.includes('area') ||
      text.includes('rectangle') ||
      text.includes('cm2') ||
      text.includes('cm²')
    ) {
      results.push({
        id: `misc_area_${Date.now()}`,
        ks2Code: '4M7',
        ks2Domain: 'Measurement & Geometry',
        title: 'Perimeter vs Area Formula Reversal',
        description:
          'Multiplying sides to find perimeter or adding boundary edges to find surface area.',
        errorPattern:
          'Calculated 2 × (L + W) when square units (L × W) were asked.',
        socraticInsight:
          'Think of the coral pool: Perimeter is walking all the way around the outside rim. Area is the flat water surface inside measured in square tiles!',
      });
    }

    // 5. Default Fallback Misconception if no specific cue matched (4N4)
    if (results.length === 0) {
      results.push({
        id: `misc_general_${Date.now()}`,
        ks2Code: '4N4',
        ks2Domain: 'Numerical Reasoning',
        title: 'Multi-Step Problem Decomposition',
        description:
          'Carrying out the first operation of a multi-step word problem but omitting the final answer transformation.',
        errorPattern:
          'Answered step 1 correctly but missed the secondary question constraint.',
        socraticInsight:
          'Underline the final question with Clawey’s golden claw! Before writing your final number, ask: "Did I answer what the question truly asked?"',
      });
    }

    return results;
  }
}
